import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { useContext, useState, useEffect } from 'react';

import { PlatformType } from '@invideoio/web-shared/constants/common';
import useIntersectionObserver from '@react-hook/intersection-observer';
import useTranslation from 'next-translate/useTranslation';
import {
  DispatchContext,
  RedirectContext,
  StateContext,
} from '../../store/store';

import ErrorBoundary from '@components/ErrorBoundary';
import ToggleDurationButton from '@components/ai-pricing/ToggleDurationButton';
import PricingPlanCard from '@components/ai-pricing/plans/PricingPlanCard';
import PaymentDebugButton from '@components/ai-pricing/PaymentDebugButton';
import PaymentDebug from '@components/ai-pricing/PaymentDebug';
import TitleH2BlockGradient from '@components/common/Title/TitleH2BlockGradient';
import useAIPricing from '@hooks/useAIPricing';
import usePublicPricing from '@hooks/usePublicPricing';
import { SimpleHeadingType } from 'src/types';
import { AiPlan, PricingDuration, PlanKey } from 'src/types/pricing.types';
import { getSeatsFromCounter } from '@components/ai-pricing/AiCredits';
import { sendRudderStackAnalyticEvent } from '@utils/analytics';
import {
  AnalyticsProductType,
  ClickCtaType,
  ClickCtaUxSource,
  ClickGetPlanEventProperties,
} from 'src/types/analytics.types';
import Shimmer from '@components/loader/Shimmer';
import StaticPricingCards from '@components/ai-pricing/plans/StaticPricingCards';

interface PricingPlanProps {
  platform: PlatformType;
  theme?: 'light' | 'dark';
  pricingTitle?: SimpleHeadingType;
}

const PricingPlanAI = (props: PricingPlanProps) => {
  const [isYearlyDuration, setToggleActive] = useState(true);
  const [debugVisible, setDebugVisible] = useState(false);
  const [shouldShowDebug, setShouldShowDebug] = useState(false);

  const { t } = useTranslation('common');
  const {
    platform,
    theme = 'dark',
    pricingTitle = {
      plainHeading: t(`pricing.title`),
      highlightedHeading: '',
    },
  } = props;
  const [sectionRef, setSectionRef] = useState<any>(null);
  const { isIntersecting } = useIntersectionObserver(sectionRef);

  const dispatch = useContext(DispatchContext);

  const globalState = useContext(StateContext);

  const { handlePricingCardClick } = useContext(RedirectContext);

  const AI_PRICING = `${process.env.NEXT_PUBLIC_BASE_HOST}/ai-pricing`;

  const { hasPlanFetched } = useAIPricing(isIntersecting);

  // Check for debug flag on client side only to avoid hydration issues
  useEffect(() => {
    // const isDebugFlag = window.location.search.includes("debug");
    const isDebugFlag = false
    setShouldShowDebug(isDebugFlag);
  }, []);

  // Shared pricing state for both main UI and debug functionality
  const {
    plans: sharedPlans,
    selectedDuration: sharedDuration,
    updateDuration: updateSharedDuration,
    updatePlanCursor,
    updatePlanBoostCursor,
    updateCursorDirectly,
    loading: pricingLoading,
  } = usePublicPricing({
    isIntersecting: true,
    initialDuration: isYearlyDuration ? PricingDuration.YEARLY : PricingDuration.MONTHLY,
  });

  // Sync the toggle state with shared pricing duration
  useEffect(() => {
    if (sharedDuration === PricingDuration.YEARLY && !isYearlyDuration) {
      setToggleActive(true);
    } else if (sharedDuration === PricingDuration.MONTHLY && isYearlyDuration) {
      setToggleActive(false);
    }
  }, [sharedDuration, isYearlyDuration]);

  // Handle duration toggle - update both UI and shared state
  const handleDurationToggle = (newIsYearly: boolean) => {
    setToggleActive(newIsYearly);
    updateSharedDuration(newIsYearly ? PricingDuration.YEARLY : PricingDuration.MONTHLY);
  };

  const handleOnCardClick = (plan: AiPlan) => () => {
    const { addOnSubscriptionPlans } = globalState;

    // TODO: Get plan2Id for plan supported_items
    const plan2Id =
      plan.key === 'plus' || plan.key === 'max'
        ? 'stock_v2_pack'
        : 'generative_v2_pack';

    const plan2 = isYearlyDuration
      ? addOnSubscriptionPlans[plan2Id].yearly.plan_id
      : addOnSubscriptionPlans[plan2Id].monthly.plan_id;

    const seats2 = getSeatsFromCounter(globalState.aiCreditsCounter[plan.key]);

    handlePricingCardClick(
      plan,
      isYearlyDuration,
      globalState.seats,
      plan2,
      seats2,
    );

    const analyticsProperties: ClickGetPlanEventProperties = {
      plan_price_id:
        plan.planPrice[isYearlyDuration ? 'yearly' : 'monthly'].planId,
      product: AnalyticsProductType.AI,
      seats: globalState.seats[plan.key] ?? -1,
      type: ClickCtaType.ClickGetPlan,
      ux_source: ClickCtaUxSource.PricingCard,
    };

    if (seats2) {
      analyticsProperties.addon_plan_price_id = plan2;
      analyticsProperties.addon_plan_seats = seats2;
    }

    sendRudderStackAnalyticEvent({
      event_name: 'click_cta',
      event_properties: analyticsProperties,
    });
  };

  return (
    <section
      id="iv-ai-pricing"
      ref={setSectionRef}
      className={ivclass(
        'xl:iv-pt-150 iv-pt-100 sm:iv-pt-75',
        theme === 'dark' && 'xl:iv-pb-150 iv-pb-100 sm:iv-pb-75',
      )}
    >
      <div
        className={ivclass(
          'iv-container iv-mx-auto',
          'pricingcollapse:iv-px-25 md:iv-px-25 sm:iv-px-20',
        )}
      >
        <div
          className={ivclass(
            'iv-col-span-12',
            'iv-grid',
            'iv-text-center iv-mb-50 sm:iv-mb-40',
          )}
        >
          <TitleH2BlockGradient heading={pricingTitle} theme={theme} />
        </div>
        <div
          className={ivclass(
            'iv-text-center',
            'xl:iv-mt-75 iv-mt-50 sm:iv-mt-20',
          )}
        >
          <div
            className={ivclass(
              'iv-flex iv-justify-center iv-items-center iv-gap-4',
            )}
          >
            {/* Debug Button */}
            {shouldShowDebug && !debugVisible && (
              <PaymentDebugButton
                onClick={() => setDebugVisible(true)}
                isVisible={true}
              />
            )}

            <ToggleDurationButton
              toggleState={isYearlyDuration}
              toggleHandler={handleDurationToggle}
            />
          </div>
        </div>

        {/* Debug Modal */}
        {shouldShowDebug && (
          <PaymentDebug
            plans={sharedPlans}
            selectedDuration={sharedDuration}
            onDurationChange={updateSharedDuration}
            onBoostChange={updatePlanBoostCursor}
            onCreditChange={updatePlanCursor}
            onCursorUpdate={updateCursorDirectly}
            isVisible={debugVisible}
            onClose={() => setDebugVisible(false)}
          />
        )}

        <div className="iv-flex iv-justify-center">
          <div
            className={ivclass(
              'iv-flex iv-justify-center',
              'sm:iv-flex-col md:iv-flex-col pricingcollapse:iv-flex-col',
              'iv-gap-30 md:iv-gap-35 sm:iv-gap-30',
              'iv-mt-10',
              'xl:iv-pb-4',
              'sm:iv-w-full',
            )}
          >
            <ErrorBoundary>
              {hasPlanFetched && Array.isArray(globalState.aiPlanData) && sharedPlans.length > 0
                ? globalState.aiPlanData.map((plan: AiPlan) => {
                    if (plan.name === 'Free') {
                      if (theme === 'light') {
                        plan.CTA.backgroundColorClass = 'iv-bg-grey-90';
                        plan.CTA.backgroundColorClass = 'iv-bg-grey-90';
                      }
                    }

                    // Find corresponding shared plan data for ladder-based pricing
                    const sharedPlan = sharedPlans.find(sp => sp.id === plan.key);
                    const currentLadder = sharedPlan?.ladders[sharedPlan.cursor];

                    const options = {
                      theme,
                      isYearlyDuration,
                      planKey: plan.key,
                      planName: plan.name,
                      planSubText: plan.subText,
                      bannerText: plan.key === 'generative' ? 'Best Value' : '',
                      planPrice: plan.planPrice,
                      CTA: plan.CTA,
                      features: plan?.key_features,
                      onClick: handleOnCardClick(plan),
                      platform: platform,
                      usageQuota: plan.usageQuota,
                      addonPlan: plan.supported_item_plans?.[0]
                        ? globalState.addOnSubscriptionPlans[
                            plan.supported_item_plans[0]
                          ]
                        : undefined,
                      // Add ladder data for price per credit display
                      ladder: currentLadder,
                      duration: sharedDuration,
                                            // Add ladder update functions for +/- buttons
                      onBoostChange: sharedPlan ? (type: 'increment' | 'decrement') =>
                        updatePlanBoostCursor(plan.key as PlanKey, type) : undefined,
                      onCreditChange: sharedPlan ? (type: 'increment' | 'decrement') =>
                        updatePlanCursor(plan.key as PlanKey, type) : undefined,
                    };

                    return <PricingPlanCard key={plan.key} {...options} />;
                  })
                : Array(4)
                    .fill(-1)
                    .map((_, idx) => (
                      <div
                        key={idx}
                        className={ivclass(
                          'iv-h-[600px] iv-w-[300px]',
                          'sm:iv-h-[300px] md:iv-w-[600px] md:iv-h-[300px]',
                        )}
                      >
                        <Shimmer />
                      </div>
                    ))}
            </ErrorBoundary>
          </div>
        </div>
      </div>
      <div
        className={ivclass(
          'iv-flex iv-justify-center iv-w-full iv-mt-20',
          'iv-mt-20 lg:iv-px-40 md:iv-px-25 iv-px-20',
          'xl:iv-ml-10 lg:iv-ml-8',
        )}
      >
        <StaticPricingCards />
      </div>
      <div className={ivclass('iv-text-center', 'xl:iv-mt-75 iv-mt-50')}>
        <span
          className={ivclass(
            'iv-inline-flex iv-justify-center iv-items-center',
            'iv-cursor-pointer',
            'iv-rounded-full',
            'iv-border-2',
            'iv-px-25 sm:iv-px-15',
            'iv-h-52 sm:iv-h-28',
            'iv-text-body-2xl sm:iv-text-body-m',
            'iv-font-semibold',
            'iv-transition-all iv-duration-150',
            theme === 'dark'
              ? 'iv-text-grey-0 iv-border-grey-0'
              : 'iv-text-blue iv-border-blue hover:iv-text-grey-0 hover:iv-bg-blue',
          )}
          onClick={() => {
            window.open(AI_PRICING, '_blank');
          }}
        >
          {t(`pricing.sectionCTA`)}
        </span>
      </div>
    </section>
  );
};

export default PricingPlanAI;
